import type { ClashResult } from '@viewer/clash/lib'
import { REDES, type Especialidade } from './clashEspecialidades'
import type { EspecialidadeDe } from './clashExclusao'

// ---------------------------------------------------------------------------
// Filtro a entrada: o primeiro passo do funil (docs/CLASH.md, seccao 11).
//
// Antes de um conflito chegar a lista, tres cortes da configuracao da
// deteccao:
//
//   folga por pares  a folga so se verifica entre os pares de
//                    especialidades escolhidos (por omissao MEP x EST e
//                    MEP x MEP). O ARQ fica fora da folga: mobiliario,
//                    portas e acabamentos perto de uma conduta nao sao
//                    falta de espaco de montagem. Um modelo sem
//                    especialidade conhecida nao se corta (nao se sabe).
//   penetracao       uma interferencia dura abaixo da penetracao minima
//                    (por omissao 10 mm, a profundidade aproximada) nao
//                    entra. Nao e a tolerancia do motor (1 mm, o que conta
//                    como tocar): e o corte da coordenacao.
//   contactos        o "Ignorar contactos" que ja existia: folgas com
//                    distancia abaixo disto sao elementos encostados.
//
// Cada corte conta, para o painel dizer "N excluidas a entrada (porque)".
// Puro.
// ---------------------------------------------------------------------------

export type FamiliaDeEspecialidade = 'MEP' | 'EST' | 'ARQ'

/** Os pares possiveis, pela ordem do ecra. */
export const PARES_DE_FOLGA = ['MEP×EST', 'MEP×MEP', 'MEP×ARQ', 'EST×EST', 'EST×ARQ', 'ARQ×ARQ'] as const
export type ParDeFolga = (typeof PARES_DE_FOLGA)[number]

export const FOLGA_PARES_POR_OMISSAO: ParDeFolga[] = ['MEP×EST', 'MEP×MEP']

/** Penetracao minima por omissao, m. */
export const PENETRACAO_MINIMA_POR_OMISSAO = 0.01

const ORDEM_FAMILIA: FamiliaDeEspecialidade[] = ['MEP', 'EST', 'ARQ']

export function familiaDe(e: Especialidade | null): FamiliaDeEspecialidade | null {
  if (!e) return null
  if (REDES.has(e)) return 'MEP'
  return e === 'EST' ? 'EST' : 'ARQ'
}

/** O par de familias, pela ordem canonica (MEP, EST, ARQ). */
export function parDeFolga(a: Especialidade | null, b: Especialidade | null): ParDeFolga | null {
  const fa = familiaDe(a)
  const fb = familiaDe(b)
  if (!fa || !fb) return null
  const [x, y] = ORDEM_FAMILIA.indexOf(fa) <= ORDEM_FAMILIA.indexOf(fb) ? [fa, fb] : [fb, fa]
  return `${x}×${y}` as ParDeFolga
}

/** Le os pares gravados. Ausente (ficheiro anterior) ou sem nenhum par
 *  valido, os de omissao: nenhum par e "sem folga", que se pede com a
 *  folga a 0. */
export function lerFolgaPares(v: unknown): ParDeFolga[] {
  if (!Array.isArray(v)) return [...FOLGA_PARES_POR_OMISSAO]
  const out = PARES_DE_FOLGA.filter((p) => v.includes(p))
  return out.length > 0 ? out : [...FOLGA_PARES_POR_OMISSAO]
}

export interface ExcluidasAEntrada {
  /** Folgas fora dos pares escolhidos, e por par. */
  folga: number
  porPar: Partial<Record<ParDeFolga, number>>
  /** Interferencias abaixo da penetracao minima. */
  penetracao: number
  /** Folgas de elementos encostados (Ignorar contactos). */
  contactos: number
  /** A configuracao usada, para o texto. */
  penetracaoMinima: number
  ignoreContacts: number
}

export function semExcluidas(): ExcluidasAEntrada {
  return { folga: 0, porPar: {}, penetracao: 0, contactos: 0, penetracaoMinima: 0, ignoreContacts: 0 }
}

export function totalExcluidas(x: ExcluidasAEntrada): number {
  return x.folga + x.penetracao + x.contactos
}

/** Aplica os tres cortes. A ordem dos resultados mantem-se. */
export function filtrarAEntrada(
  results: readonly ClashResult[],
  o: {
    especialidadeDe: EspecialidadeDe
    folgaPares: readonly string[]
    /** m; 0 desliga. */
    penetracaoMinima: number
    /** m; 0 desliga. */
    ignoreContacts?: number
  },
): { mantidos: ClashResult[]; excluidas: ExcluidasAEntrada } {
  const ex: ExcluidasAEntrada = {
    ...semExcluidas(),
    penetracaoMinima: o.penetracaoMinima,
    ignoreContacts: o.ignoreContacts ?? 0,
  }
  const pares = new Set(o.folgaPares)
  const mantidos: ClashResult[] = []
  for (const r of results) {
    if (r.kind === 'clearance') {
      if ((o.ignoreContacts ?? 0) > 0 && (r.distance ?? 0) < (o.ignoreContacts ?? 0)) {
        ex.contactos += 1
        continue
      }
      const par = parDeFolga(o.especialidadeDe(r.pair.modelA), o.especialidadeDe(r.pair.modelB))
      if (par && !pares.has(par)) {
        ex.folga += 1
        ex.porPar[par] = (ex.porPar[par] ?? 0) + 1
        continue
      }
    } else if (o.penetracaoMinima > 0 && typeof r.depth === 'number' && r.depth < o.penetracaoMinima) {
      ex.penetracao += 1
      continue
    }
    mantidos.push(r)
  }
  return { mantidos, excluidas: ex }
}

function mm(m: number): string {
  return `${String(Math.round(m * 1000 * 100) / 100).replace('.', ',')} mm`
}

/** "N excluídas à entrada: ..." (vazio quando nada foi excluído). */
export function textoDasExcluidas(x: ExcluidasAEntrada): string {
  const total = totalExcluidas(x)
  if (total === 0) return ''
  const partes: string[] = []
  if (x.folga > 0) {
    const porPar = PARES_DE_FOLGA.filter((p) => x.porPar[p])
      .map((p) => `${p} ${x.porPar[p]}`)
      .join(', ')
    partes.push(`${x.folga} folga${x.folga === 1 ? '' : 's'} fora dos pares escolhidos (${porPar})`)
  }
  if (x.penetracao > 0) {
    partes.push(
      `${x.penetracao} interferência${x.penetracao === 1 ? '' : 's'} com penetração abaixo de ${mm(x.penetracaoMinima)}`,
    )
  }
  if (x.contactos > 0) {
    partes.push(`${x.contactos} contacto${x.contactos === 1 ? '' : 's'} (folga abaixo de ${mm(x.ignoreContacts)})`)
  }
  return `${total} excluída${total === 1 ? '' : 's'} à entrada: ${partes.join('; ')}.`
}

/** O que a configuracao vai cortar, antes de correr. */
export function textoDoFunilAntesDeCorrer(o: {
  clearance: number
  folgaPares: readonly string[]
  penetracaoMinima: number
  ignoreContacts: number
}): string {
  const partes: string[] = []
  if (o.penetracaoMinima > 0) partes.push(`interferências abaixo de ${mm(o.penetracaoMinima)} não entram`)
  if (o.clearance > 0) {
    partes.push(`folga só entre ${o.folgaPares.join(', ')}`)
    if (o.ignoreContacts > 0) partes.push(`contactos abaixo de ${mm(o.ignoreContacts)} não entram`)
  }
  return partes.length > 0 ? `À entrada: ${partes.join('; ')}.` : ''
}
